import { CohortPageRoutes } from '@/constants/PageRoutes';
import React, { ReactNode, useState } from 'react';
import { GetStaticProps } from 'next';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Legal } from '@/components/Legal';
import { Mode } from '@/components/Mode';
import { ThemeProvider } from '@/components/ThemeProvider';
import { fetchRates } from '@/queries/fetchRates';
import { MoreLess } from '@/components/MoreLess';
import { WhyAthena } from '@/components/WhyAthena/WhyAthena';
import { WHY_ATHENA_REFINANCING_CONTENT, WHY_ATHENA_SECONDARY_PROOF_POINTS } from 'content/INVRefi/whyAthenaContent';
import { useModalState } from '@/components/cohort/hooks/useModalState';
import { Modal } from '@/components/cohort/Modal/Modal';
import { PageTitle } from '@/components/cohort/pageTitle/PageTitle';
import { HowItWorks } from '@/components/cohort/HowItWorks/HowItWorks';
import { NavigationSecondary, scrollMarginTop } from '@/components/navigationSecondary/NavigationSecondary';
import dynamic from 'next/dynamic';
import { HowDoI } from '@/components/cohort/HowDoI/HowDoI';
import { MaxWidthContainer } from '@/components/cohort/MaxWidthContainer/MaxWidthContainer';
import { ContainerSizes } from '@/constants/ContainerSizes';
import { BlockRenderer } from '@/components/BlockRenderer';
import { TrustpilotContactUs } from '@/components/cohort/TrustpilotContactUs/TrustpilotContactUs';
import { carouselCardContent } from 'content/INVRefi/carouselModalContent';
import { HowItWorksContent } from 'content/INVRefi/howItWorksContent';
import { fetchPageContent } from '@/queries/fetchPageContent';
import { processMarkdown } from '../index';
import { SEO } from '../../components/SEO';
import type { NextPageWithLayout } from '../../types/layoutExtensions';
import { useScrollAnchorIntoView } from '../../utilities/useScrollAnchorIntoView';
import RefinancingCarousel from '../../components/cohort/RefinancingCarousel';
import styles from './refinancing.module.scss';
import { SuperScript } from '../../components/cohort/SuperScript';
import { InvestorExpertContent, MaxReturnsContent, LoanStructuringContent } from '../../content/INVRefi/howDoIContent';

const RateCalculator = dynamic(
  () => import('@athenamortgages/athena-calculator-components').then(comp => comp.RateCalculator),
  { ssr: false },
);

const InvestorsRefinancing: NextPageWithLayout = page => {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  const [modalContent, setModalContent] = useState<ReactNode | null>(null);
  const { isOpen, openModal, closeModal } = useModalState();
  useScrollAnchorIntoView();

  const { rates, pageCollection } = page;
  const { contentCollection, legal } = pageCollection.items[0];

  const displayRate = rates['direct-basics'].VARIABLE_RATE.PRINCIPAL_AND_INTEREST.INVESTMENT['0-50'].rate * 100;

  return (
    <>
      <Navigation mode={Mode.LIGHT as Mode} scroll={false} />
      <main id="skip-to-content" style={{ outline: 'none' }} tabIndex={-1}>
        <Modal open={isOpen} closeModal={closeModal}>
          {modalContent}
        </Modal>
        <MaxWidthContainer className={`${styles.pageTitle}`} size={ContainerSizes.EXTRA_LARGE}>
          <PageTitle
            titleEyebrow="REFINANCING YOUR INVESTMENT PROPERTY"
            title={
              <span>
                Welcome to the
                <br />
                home of investors
              </span>
            }
            cohort="investor"
            rate={displayRate}
          />

          <RefinancingCarousel cards={carouselCardContent} />

          <div className={styles.secondaryNavigation}>
            <NavigationSecondary>
              {[
                {
                  text: 'Why Athena',
                  target: 'why',
                },
                {
                  text: 'Our loans',
                  target: 'loans',
                },
                {
                  text: 'How do I...',
                  target: 'how',
                },
                {
                  text: 'Calculate now',
                  target: 'steps',
                },
                {
                  text: 'Articles & FAQs',
                  target: 'learn',
                },
              ]}
            </NavigationSecondary>
          </div>

          <section id="why" style={{ scrollMarginTop }}>
            <WhyAthena
              primaryProofPoints={WHY_ATHENA_REFINANCING_CONTENT}
              secondaryProofPoints={WHY_ATHENA_SECONDARY_PROOF_POINTS}
              setModalContent={setModalContent}
              openModal={openModal}
            />
          </section>
        </MaxWidthContainer>

        <section id="loans" style={{ minHeight: 800, scrollMarginTop }}>
          {/* Temporary workaround for the comment below */}
          <div className={styles.rateCalculatorContainer}>
            <div className={styles.rateCalculatorTitle}>
              <h2>Our loans for Investors</h2>
              <p>Simple or sophisticated, stay great rates - we&rsquo;ve got a loan for your property.</p>
            </div>
            <ThemeProvider>
              <RateCalculator
                isDarkMode
                ratesAnchorProps={{ isVisible: false }}
                areCardCTAsVisible={false}
                areLoanTypeRadioButtonsVisible={false}
                /* NOTE: Leaving this here for now but unsure what the use case for it was */
                /* The below breaks the layout for components below on resize...revisit this */
                // absolutelyPositionedBackgroundElement={
                //   <div
                //     style={{
                //       position: 'absolute',
                //       width: '100%',
                //       height: '1000px',
                //       zIndex: -1,
                //       backgroundColor: '#3B015F',
                //     }}
                //   />
                // }
                rates={{
                  directBasicsRates: rates['direct-basics'],
                  directExtrasRates: rates['direct-extras'],
                  selectRates: rates.select,
                }}
                forwardButtonProps={{
                  isVisible: true,
                  buttonText: 'See all of our loans',
                  to: '/our-loans',
                }}
                defaultInputs={{
                  interestRateType: 'VARIABLE_RATE',
                  loanPurpose: 'INVESTMENT',
                  repaymentType: 'PRINCIPAL_AND_INTEREST',
                  lvr: 0.5,
                }}
              />
            </ThemeProvider>
          </div>
        </section>

        <section id="how" style={{ scrollMarginTop }}>
          <HowDoI segments={[MaxReturnsContent, InvestorExpertContent, LoanStructuringContent]} />
        </section>
        {/* CUTE-239 - Trust Pilot */}

        <MoreLess />

        <section id="steps" style={{ scrollMarginTop }}>
          <HowItWorks content={HowItWorksContent} />
        </section>

        {/* CUTE-239 - Trust Pilot */}
        <TrustpilotContactUs
          trustpilot={{
            heading: (
              <>
                Real Investors.
                <br />
                Real wins.
              </>
            ),
            subheading: 'Join the thousands of happy Athena customers and hear their stories on Trustpilot.',
          }}
          contactUs={{
            heading: 'Talk to Investor Experts',
            subheading: (
              <>
                Talk to our dedicated Investor A-team about unlocking equity, structuring your loans or maximising your
                borrowing power
                <SuperScript>6</SuperScript>. They&rsquo;re ready to help via rapid response SMS or live chat so you
                don&rsquo;t need to wait on hold! If you&rsquo;d prefer a phone convo, they&rsquo;d love to talk.
              </>
            ),
            buttonText: 'Get in touch',
          }}
        />

        <section id="learn" style={{ scrollMarginTop }}>
          {/* CMS Page has Article List and FAQs under `content` property, which we run through the block renderer */}
          {contentCollection?.items.map((contentBlock, index) => (
            <BlockRenderer {...contentBlock} key={index} rates={rates} />
          ))}
        </section>

        {legal && legal.legalHeading && (
          <Legal
            legalHeading={legal.legalHeading}
            legalComparisonBlock={legal.legalComparisonBlock}
            accordians={legal.accordiansCollection.items[0]}
          />
        )}
      </main>
      <Footer />
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const url = CohortPageRoutes.INVESTORS.REFINANCING;
  const rates = await fetchRates();
  const { data }: any = await fetchPageContent(url, false);
  const page = {
    url,
    rates,
    ...data,
  };
  await processMarkdown(page.pageCollection.items[0]);
  return {
    props: page,
  };
};

InvestorsRefinancing.getLayout = (page: React.ReactElement) => {
  const { seoTitle, seoDescription, ldJsonSchema, ogImage } = page.props.children[1].props.pageCollection.items[0];
  const { url } = page.props.children[1].props;
  return (
    <>
      <SEO
        {...{ seoTitle, seoDescription, ldJsonSchema }}
        {...(ogImage ? { ogImageUrl: ogImage.url, ogImageWidth: ogImage.width, ogImageHeight: ogImage.height } : {})}
        fullPath={`${url}`}
      />
      <div className="page">{page}</div>
    </>
  );
};

export default InvestorsRefinancing;
